import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Sparkles, Loader2, ChevronDown, ChevronUp, Lightbulb, TrendingUp, AlertCircle, CheckCircle, Target } from 'lucide-react'
import { ReviewPreparationInsights } from '@/services/aiService'

interface AIReviewAssistantProps {
  insights: ReviewPreparationInsights | null
  isLoading: boolean
  onGenerateInsights: () => void
  employeeName: string
}

export function AIReviewAssistant({ 
  insights, 
  isLoading, 
  onGenerateInsights,
  employeeName 
}: AIReviewAssistantProps) {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [expandedKPAs, setExpandedKPAs] = useState<Set<string>>(new Set())

  const toggleKPA = (kpaId: string) => {
    const newExpanded = new Set(expandedKPAs)
    if (newExpanded.has(kpaId)) {
      newExpanded.delete(kpaId)
    } else {
      newExpanded.add(kpaId)
    }
    setExpandedKPAs(newExpanded)
  }

  const getReadinessColor = (score: number) => {
    if (score >= 80) return 'bg-green-500'
    if (score >= 60) return 'bg-blue-500'
    if (score >= 40) return 'bg-amber-500'
    return 'bg-red-500'
  }

  const getReadinessBadgeVariant = (score: number): "default" | "secondary" | "destructive" | "outline" => {
    if (score >= 80) return 'default'
    if (score >= 60) return 'secondary'
    return 'outline'
  }

  return (
    <div>
      {/* Compact Collapsed Banner */}
      {insights && isCollapsed && (
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg px-4 py-2 flex items-center justify-between shadow-sm mb-4" style={{ textShadow: 'none' }}>
          <div className="flex items-center gap-3">
            <Sparkles className="h-4 w-4 text-white" />
            <span className="text-white font-medium text-sm">AI Check-in Preparation</span>
            <div className="flex items-center gap-3 ml-2">
              <div className="bg-white/20 rounded-full px-3 py-1">
                <span className="text-white text-sm font-semibold">{insights.overallReadiness}%</span>
                <span className="text-white/80 text-xs ml-1">Readiness Score</span>
              </div>
              <span className="text-white/90 text-sm">{insights.evidenceQuality.total} impact items • {insights.kpaReadiness.length} KPAs analysed</span>
              {insights.recommendedFocus.length > 0 && (
                <span className="text-amber-200 text-sm font-medium">• {insights.recommendedFocus.length} focus areas recommended</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => setIsCollapsed(false)}
              className="text-white hover:bg-white/20 h-7"
            >
              View Details
            </Button>
            <Button 
              variant="ghost"
              size="sm"
              onClick={onGenerateInsights}
              disabled={isLoading}
              className="text-white hover:bg-white/20 h-7"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="text-white hover:bg-white/20 h-7"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Initial State - No Insights - Compact Banner */}
      {!insights && (
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg px-4 py-2 flex items-center justify-between shadow-sm mb-4" style={{ textShadow: 'none' }}>
          <div className="flex items-center gap-3">
            <Sparkles className="h-4 w-4 text-white" />
            <span className="text-white font-medium text-sm">AI Check-in Preparation</span>
            <span className="text-white/90 text-sm">Impact-based insights to support your check-in discussion.</span>
          </div>
          <Button 
            variant="ghost"
            size="sm"
            onClick={onGenerateInsights}
            disabled={isLoading}
            className="bg-white/20 hover:bg-white/30 text-white h-7"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analysing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Insights
              </>
            )}
          </Button>
        </div>
      )}

      {/* Expanded Content */}
      {insights && !isCollapsed && (
        <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50 mb-4" style={{ textShadow: 'none' }}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-5 w-5 text-indigo-600" />
                  AI Check-in Preparation
                </CardTitle>
                <CardDescription className="mt-1 text-sm">
                  Impact-based insights to support your check-in discussion.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={onGenerateInsights}
                  disabled={isLoading}
                  className="border-indigo-300 text-indigo-700"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analysing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Refresh Insights
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsCollapsed(true)}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 max-h-[600px] overflow-y-auto">
            {/* Overall Readiness Score */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border-2 border-purple-200">
            <div>
              <h4 className="font-semibold text-lg">Overall Check-in Readiness</h4>
              <p className="text-sm text-gray-600">Based on impact portfolio quality and completeness</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-3xl font-bold text-purple-700">{insights.overallReadiness}%</div>
                <div className="text-xs text-gray-500">Readiness Score</div>
              </div>
              <div className={`h-16 w-16 rounded-full ${getReadinessColor(insights.overallReadiness)} flex items-center justify-center`}>
                {insights.overallReadiness >= 70 ? (
                  <CheckCircle className="h-8 w-8 text-white" />
                ) : (
                  <AlertCircle className="h-8 w-8 text-white" />
                )}
              </div>
            </div>
          </div>

          {/* Recommended Focus Areas */}
          {insights.recommendedFocus.length > 0 && (
            <div className="p-4 bg-amber-50 border-l-4 border-l-amber-500 rounded">
              <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Recommended Focus Areas
              </h4>
              <ul className="space-y-2">
                {insights.recommendedFocus.map((focus, i) => (
                  <li key={i} className="text-sm text-amber-800 flex items-start gap-2">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span>{focus}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Impact Portfolio Quality Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-blue-50 rounded border border-blue-200">
              <div className="text-2xl font-bold text-blue-700">{insights.evidenceQuality.total}</div>
              <div className="text-xs text-blue-600">Total Impact Items</div>
            </div>
            <div className="p-3 bg-green-50 rounded border border-green-200">
              <div className="text-2xl font-bold text-green-700">{insights.evidenceQuality.withMeasurements}</div>
              <div className="text-xs text-green-600">With Measurements</div>
            </div>
            <div className="p-3 bg-purple-50 rounded border border-purple-200">
              <div className="text-2xl font-bold text-purple-700">{insights.evidenceQuality.qualityScore}%</div>
              <div className="text-xs text-purple-600">Quality Score</div>
            </div>
          </div>

          {/* Evidence Improvement Suggestions */}
          {insights.evidenceQuality.suggestions.length > 0 && (
            <div className="p-4 bg-blue-50 border-l-4 border-l-blue-500 rounded">
              <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                <Lightbulb className="h-4 w-4" />
                Impact Portfolio Improvement Tips
              </h4>
              <ul className="space-y-1">
                {insights.evidenceQuality.suggestions.map((suggestion, i) => (
                  <li key={i} className="text-sm text-blue-800">• {suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {/* KPA Readiness Details */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Target className="h-4 w-4" />
              KPA Readiness Details
            </h4>
            {insights.kpaReadiness.map((kpa) => (
              <Card key={kpa.kpaId} className="border border-gray-200">
                <button 
                  className="w-full p-4 hover:bg-gray-50 transition-colors"
                  onClick={() => toggleKPA(kpa.kpaId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`h-12 w-12 rounded-full ${getReadinessColor(kpa.readinessScore)} flex items-center justify-center text-white font-bold`}>
                        {kpa.readinessScore}
                      </div>
                      <div className="text-left">
                        <div className="font-medium">{kpa.kpaName}</div>
                        <div className="text-xs text-gray-500">
                          {kpa.strengths.length} strengths • {kpa.gaps.length} areas to discuss
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={getReadinessBadgeVariant(kpa.readinessScore)}>
                        {kpa.readinessScore >= 80 ? 'Excellent' : kpa.readinessScore >= 60 ? 'Good' : 'Needs Attention'}
                      </Badge>
                      {expandedKPAs.has(kpa.kpaId) ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </button>
                
                {expandedKPAs.has(kpa.kpaId) && (
                  <div className="px-4 pb-4 space-y-4 border-t pt-4">
                    {/* Strengths */}
                      {kpa.strengths.length > 0 && (
                        <div>
                          <h5 className="font-medium text-sm mb-2 text-green-700 flex items-center gap-1">
                            <CheckCircle className="h-4 w-4" />
                            Key Strengths
                          </h5>
                          <ul className="space-y-1">
                            {kpa.strengths.map((strength, i) => (
                              <li key={i} className="text-sm text-gray-700 pl-5 relative">
                                <span className="absolute left-0 text-green-600">✓</span>
                                {strength}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Gaps */}
                      {kpa.gaps.length > 0 && (
                        <div>
                          <h5 className="font-medium text-sm mb-2 text-amber-700 flex items-center gap-1">
                            <AlertCircle className="h-4 w-4" />
                            Areas for Discussion
                          </h5>
                          <ul className="space-y-1">
                            {kpa.gaps.map((gap, i) => (
                              <li key={i} className="text-sm text-gray-700 pl-5 relative">
                                <span className="absolute left-0 text-amber-600">⚠</span>
                                {gap}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Discussion Points */}
                      {kpa.suggestedDiscussionPoints.length > 0 && (
                        <div className="bg-purple-50 p-3 rounded">
                          <h5 className="font-medium text-sm mb-2 text-purple-700 flex items-center gap-1">
                            <Lightbulb className="h-4 w-4" />
                            AI-Suggested Discussion Starters
                          </h5>
                          <ul className="space-y-1">
                            {kpa.suggestedDiscussionPoints.map((point, i) => (
                              <li key={i} className="text-sm text-purple-900">• {point}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </div>
                )}
              </Card>
            ))}
          </div>

          {/* Generated timestamp */}
          <div className="text-xs text-gray-500 text-center pt-2 border-t">
            Insights generated at {new Date(insights.generatedAt).toLocaleString()}
          </div>
        </CardContent>
        </Card>
      )}
    </div>
  )
}
